import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IArchivedTaskDTO,
  IArchivingJobExperimentResultDTO,
} from 'business/models/Models';
import { JobTypeDTO } from 'components/main/Setup/Imaging/ImageManagementTask/constants';
import { BackgroundJobDTO } from 'components/backgroundjobs/backgroundJobs';
import {
  fetchArchivedTasksThunk,
  fetchArchivedExperimentsThunk,
  runUnarchiveThunk,
  fetchActiveUnarchiveJobResultsThunk,
  fetchUndismissedUnarchivingJobResultsThunk,
} from 'redux/thunks/unarchiving';
import {
  getArchiveCurrentLocation,
  getArchivedExperimentIds,
  getLastRestoredExperimentId,
  markUnarchivedResults,
  mergeUnarchivingResults,
  UnarchiveResultRow,
} from 'redux/utils/archivingProgressUtils';
import { upsertBackgroundJob } from './backgroundJobsReducer';

type AsyncCollection<T> = {
  data: T[];
  isLoading: boolean;
  error: string | null;
};

type UnarchivingState = {
  archivedTasks: AsyncCollection<IArchivedTaskDTO>;
  previewExperiments: AsyncCollection<IArchivingJobExperimentResultDTO>;
  unarchivingResults: UnarchiveResultRow[];
};

const emptyCollection = <T>(): AsyncCollection<T> => ({
  data: [],
  isLoading: false,
  error: null,
});

const initialState: UnarchivingState = {
  archivedTasks: emptyCollection<IArchivedTaskDTO>(),
  previewExperiments: emptyCollection<IArchivingJobExperimentResultDTO>(),
  unarchivingResults: [],
};

const UnarchivingSlice = createSlice({
  name: 'unarchiving',
  initialState,
  reducers: {
    clearPreviewExperiments(state) {
      state.previewExperiments = emptyCollection<IArchivingJobExperimentResultDTO>();
    },
    clearUnarchivingResults(state) {
      state.unarchivingResults = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchArchivedTasksThunk.pending, (state) => {
      state.archivedTasks.isLoading = true;
      state.archivedTasks.error = null;
    });
    builder.addCase(fetchArchivedTasksThunk.fulfilled, (state, action) => {
      state.archivedTasks.data = action.payload;
      state.archivedTasks.isLoading = false;
      state.archivedTasks.error = null;
    });
    builder.addCase(fetchArchivedTasksThunk.rejected, (state, action) => {
      state.archivedTasks.isLoading = false;
      state.archivedTasks.error =
        (action.payload as string) || 'Failed to fetch archived tasks';
      state.archivedTasks.data = [];
    });

    builder.addCase(fetchArchivedExperimentsThunk.pending, (state) => {
      state.previewExperiments.isLoading = true;
      state.previewExperiments.error = null;
    });
    builder.addCase(fetchArchivedExperimentsThunk.fulfilled, (state, action) => {
      state.previewExperiments.data = action.payload;
      state.previewExperiments.isLoading = false;
      state.previewExperiments.error = null;
    });
    builder.addCase(fetchArchivedExperimentsThunk.rejected, (state, action) => {
      state.previewExperiments.isLoading = false;
      state.previewExperiments.error =
        (action.payload as string) || 'Failed to fetch archived experiments';
      state.previewExperiments.data = [];
    });

    builder.addCase(runUnarchiveThunk.fulfilled, (state, action) => {
      state.unarchivingResults = mergeUnarchivingResults(
        state.unarchivingResults,
        action.payload.value as UnarchiveResultRow[] | null,
      );
    });

    builder.addCase(fetchActiveUnarchiveJobResultsThunk.fulfilled, (state, action) => {
      if (action.payload.value != null) {
        state.unarchivingResults = action.payload.value as UnarchiveResultRow[];
      }
    });

    builder.addCase(fetchUndismissedUnarchivingJobResultsThunk.fulfilled, (state, action) => {
      state.unarchivingResults = (action.payload.value ?? []) as UnarchiveResultRow[];
    });

    builder.addMatcher(
      (action): action is PayloadAction<BackgroundJobDTO> =>
        action.type === upsertBackgroundJob.type,
      (state, action) => {
        if (Number(action.payload.jobType) !== JobTypeDTO.Unarchive) {
          return;
        }
        const restoredExperimentIds = getArchivedExperimentIds(action.payload.dataObj);
        const currentLocation = getArchiveCurrentLocation(action.payload.dataObj);
        const lastRestoredExperimentId = getLastRestoredExperimentId(
          action.payload.dataObj,
        );
        markUnarchivedResults(
          state.unarchivingResults,
          restoredExperimentIds,
          currentLocation,
          lastRestoredExperimentId,
        );
      },
    );
  },
});

export const { clearPreviewExperiments, clearUnarchivingResults } =
  UnarchivingSlice.actions;

export default UnarchivingSlice.reducer;
